import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";

import { useAuth } from "../contexts/AuthContext";
import { useFetch } from "./useFetch";

import { ApiError } from "../types/exception/errorStatus";
import { SliceResponse, SortDirectionType, SortType } from "../types/listing";
import { getErrorMessage } from "../utils/exception/exceptionUtils";

type Params = {
  page: number;
  size: number;
  search?: string;
  sort?: string;
  direction?: SortDirectionType;
};

export function usePaginatedFetch<T>(
  endpoint: string,
  params: Params,
  refreshTrigger?: boolean,
) {
  const [data, setData] = useState<SliceResponse<T> | null>(null);
  const [error, setError] = useState<string | null>(null);
  const { authInfo } = useAuth();
  const { fetcher } = useFetch(authInfo?.accessToken, authInfo?.refreshToken);
  const [loading, setLoading] = useState(true);
  const { t } = useTranslation("exceptions");
  const jsonParams = JSON.stringify(params);

  useEffect(() => {
    const fetchData = async () => {
      setLoading(true);
      setError(null);

      const query = new URLSearchParams({
        page: String(params.page),
        size: String(params.size),
        search: params.search ?? "",
        sort: params.sort ?? SortType.DATE,
        direction: params.direction ?? SortDirectionType.ASC,
      });

      for (const key in params) {
        if (!["page", "size", "search", "sort", "direction"].includes(key)) {
          query.append(key, String(params[key as keyof Params] ?? ""));
        }
      }

      try {
        const response = await fetcher<SliceResponse<T> | null>(
          `${endpoint}?${query.toString()}`,
        );
        setData(response);
      } catch (caughtError) {
        console.error("Fetch error:", caughtError);
        if (caughtError instanceof ApiError) {
          setError(getErrorMessage(caughtError.data));
        } else {
          setError(t("exceptions:unknown"));
        }
      } finally {
        setLoading(false);
      }
    };
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [
    endpoint,
    params.page,
    params.size,
    params.search,
    params.sort,
    params.direction,
    jsonParams,
    refreshTrigger,
  ]);

  return { data, loading, error };
}
